/**
 * Kontroll's two dialogs (V7.5.6, #275 D096, D097, D141): the batch approval, naming the scope, the hours and
 * every record before anything is sent, focus on the close button; and «Legg til registrering», the actual
 * clock times of one person with a required reason (POST /admin/shifts as the company's «Stemplet uten plan»
 * type, published), saved as ready, never approved. Both are modal, 580 px, with Escape and the close button
 * as safe ways out; a dirty new registration asks before it closes (shell/leave.tsx).
 */
import { errorText } from '@/api/client';
import type { ApiShift, ApiUser } from '@/api/data';
import { T } from '@/i18n';
import { osloISO, today } from '@/rules';
import { useDirty } from '@/shell/context';
import { Button } from '@/ui';
import { fullName, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import * as Dialog from '@radix-ui/react-dialog';
import { type ReactNode, useRef, useState } from 'react';
import { ReportReceipts } from './ReportReceipts';
import { dateShort, hoursText } from './kfmt';

function Frame({
  open,
  title,
  onClose,
  children,
  initial,
  testid,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  initial?: 'close';
  testid?: string;
}) {
  const close = useRef<HTMLButtonElement>(null);
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="scrim" />
        <Dialog.Content
          className="kdlg"
          aria-describedby={undefined}
          data-testid={testid}
          onOpenAutoFocus={(e) => {
            if (initial === 'close') {
              e.preventDefault();
              close.current?.focus();
            }
          }}
        >
          <header>
            <Dialog.Title asChild>
              <h2>{title}</h2>
            </Dialog.Title>
            <Dialog.Close asChild>
              <button ref={close} type="button" className="btn kdlgx" aria-label={T('Lukk dialog')}>
                <Icon name="close" />
              </button>
            </Dialog.Close>
          </header>
          <div className="kdlgb">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function BatchDialog({
  shifts,
  scope,
  hoursOf,
  nameOf,
  busy,
  onCancel,
  onConfirm,
}: {
  shifts: ApiShift[] | null;
  scope: string;
  hoursOf: (s: ApiShift) => number | null;
  nameOf: (s: ApiShift) => string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const list = shifts ?? [];
  const total = list.reduce((a, s) => a + (hoursOf(s) ?? 0), 0);
  const what = T('{n, plural, one {# registrering} other {# registreringer}}', { n: list.length });
  return (
    <Frame
      open={!!shifts}
      title={T('Godkjenn {what}?', { what })}
      onClose={onCancel}
      initial="close"
      testid="approve-confirm"
    >
      <p>{T('{scope}. Bare valgte, klare registreringer blir godkjent.', { scope })}</p>
      <dl className="kfacts">
        <div>
          <dt>{T('Arbeidstid')}</dt>
          <dd>{`${num(total)} t`}</dd>
        </div>
      </dl>
      <ul className="kbulk">
        {list.map((s) => (
          <li key={s.id}>
            {nameOf(s)}
            <span>{`${dateShort(s.start_time)}, ${hoursText(hoursOf(s))}`}</span>
          </li>
        ))}
      </ul>
      <div className="kdlgf">
        <Button onClick={onCancel}>{T('Avbryt')}</Button>
        <Button tone="pri" disabled={busy || !list.length} onClick={onConfirm}>
          {T('Godkjenn {what}', { what })}
        </Button>
      </div>
    </Frame>
  );
}

/**
 * The report-only finish (#295 ONB-16, delivery contract C07): exactly the approved records of the scope on
 * screen, named before anything is made. «Lag rapport» keeps a receipt on the server first (the records at
 * the revision shown); a record changed since this preview is refused there and nothing downloads. No
 * payroll system, employee number or pay code is asked for, and the dialog says the report is not pay.
 */
export function ReportDialog({
  shifts,
  left,
  scope,
  period,
  from,
  to,
  hoursOf,
  nameOf,
  busy,
  onCancel,
  onConfirm,
}: {
  shifts: ApiShift[] | null;
  /** records in the scope that are not approved and stay out of the report */
  left: number;
  scope: string;
  period: string;
  /** the period's first and last day: the reports already made for it are listed under the preview */
  from: string;
  to: string;
  hoursOf: (s: ApiShift) => number | null;
  nameOf: (s: ApiShift) => string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const list = shifts ?? [];
  const total = list.reduce((a, s) => a + (hoursOf(s) ?? 0), 0);
  const unknown = list.filter((s) => hoursOf(s) == null).length;
  const people = new Map<number, { name: string; n: number; h: number }>();
  for (const s of list) {
    const p = people.get(s.user_id as number) ?? { name: nameOf(s), n: 0, h: 0 };
    p.n += 1;
    p.h += hoursOf(s) ?? 0;
    people.set(s.user_id as number, p);
  }
  const rows = [...people.values()].sort((a, b) => a.name.localeCompare(b.name, 'nb'));
  return (
    <Frame
      open={!!shifts}
      title={T('Rapport over godkjente timer')}
      onClose={onCancel}
      initial="close"
      testid="report-preview"
    >
      <p>{T('{scope}. Bare godkjente registreringer i utvalget er med.', { scope })}</p>
      {list.length === 0 ? (
        <p className="khint">
          {T('Ingen godkjente registreringer i utvalget. Godkjenn registreringer først.')}
        </p>
      ) : (
        <>
          <dl className="kfacts">
            <div>
              <dt>{T('Periode')}</dt>
              <dd>{period}</dd>
            </div>
            <div>
              <dt>{T('Registreringer')}</dt>
              <dd>{list.length}</dd>
            </div>
            <div>
              <dt>{T('Ansatte')}</dt>
              <dd>{rows.length}</dd>
            </div>
            <div>
              <dt>{T('Godkjente timer')}</dt>
              <dd>{`${num(total)} t`}</dd>
            </div>
          </dl>
          <ul className="kbulk" data-testid="report-people">
            {rows.map((p) => (
              <li key={p.name}>
                {p.name}
                <span>{`${T('{n, plural, one {# registrering} other {# registreringer}}', { n: p.n })}, ${num(p.h)} t`}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {left > 0 && (
        <p className="khint">
          {T(
            '{n, plural, one {# registrering} other {# registreringer}} i utvalget er ikke godkjent og er ikke med.',
            {
              n: left,
            },
          )}
        </p>
      )}
      {unknown > 0 && (
        <p className="khint">
          {T(
            '{n, plural, one {# registrering har} other {# registreringer har}} ukjent arbeidstid. Timene står tomme i rapporten, ikke som 0.',
            {
              n: unknown,
            },
          )}
        </p>
      )}
      <p className="khint">
        {T(
          'Rapporten trenger ikke lønnssystem, ansattnummer eller lønnsarter. Den er ikke lønnskjøring eller utbetaling.',
        )}
      </p>
      {shifts && <ReportReceipts from={from} to={to} title={T('Rapporter laget for perioden')} />}
      <div className="kdlgf">
        <Button onClick={onCancel}>{T('Avbryt')}</Button>
        <Button tone="pri" disabled={busy || !list.length} onClick={onConfirm} data-testid="report-make">
          {T('Lag rapport')}
        </Button>
      </div>
    </Frame>
  );
}

export interface NewRegistration {
  user_id: number;
  punch_in_time: string;
  punch_out_time: string;
  change_reason: string;
}

/**
 * The day «Legg til registrering» opens on: the shown month's first day, as the prototype, never a day
 * before the company's start date (#288 review), and never after today: a registration is work done. A start
 * date still ahead (a demo company set up for later that already has hours) is not used (regression review
 * 2026-11-04).
 */
export function addDay(month: string, startDate: string | null | undefined, now: string): string {
  const first = `${month}-01`;
  const start = startDate?.slice(0, 10);
  const day = start && start > first && start <= now ? start : first;
  return day > now ? now : day;
}

export function AddDialog({
  open,
  people,
  person,
  month,
  startDate,
  onClose,
  onSave,
}: {
  open: boolean;
  people: ApiUser[];
  person: number | null;
  /** YYYY-MM: the times are suggested on addDay() at 08:00 and 16:00, as the prototype */
  month: string;
  /** the company's start date (Kom i gang), YYYY-MM-DD */
  startDate?: string | null | undefined;
  onClose: () => void;
  onSave: (r: NewRegistration) => Promise<void>;
}) {
  const day = addDay(month, startDate, today());
  const start = {
    who: String(person ?? people[0]?.id ?? ''),
    in: `${day}T08:00`,
    out: `${day}T16:00`,
    reason: '',
  };
  const [v, setV] = useState(start);
  // the prefilled times are a suggestion, said so until either is changed
  const suggested = v.in === start.in && v.out === start.out;
  // untouched suggested times are saved only when the manager says they are the real ones (#288 review)
  const [confirmed, setConfirmed] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dirty =
    open && (v.who !== start.who || v.in !== start.in || v.out !== start.out || !!v.reason.trim());
  useDirty('kontroll-add', dirty, T('Ny registrering'));
  const reset = () => {
    setV(start);
    setConfirmed(false);
    setErr(null);
  };
  async function save() {
    const pin = v.in ? osloISO(v.in.slice(0, 10), v.in.slice(11, 16)) : '';
    const pout = v.out ? osloISO(v.out.slice(0, 10), v.out.slice(11, 16)) : '';
    const who = v.who || start.who;
    const problem = !who
      ? T('Velg hvem registreringen gjelder.')
      : !pin || !pout
        ? T('Fyll inn både inn- og utstempling. Bruk bekreftede tider.')
        : !(Date.parse(pout) > Date.parse(pin))
          ? T('Utstempling må være etter innstempling. Kontroller også datoen.')
          : v.reason.trim().length < 3
            ? T('Skriv hvorfor registreringen legges til.')
            : suggested && !confirmed
              ? T('Tidene er et forslag. Endre dem, eller kryss av for at de stemmer.')
              : null;
    if (problem) return setErr(problem);
    setSaving(true);
    setErr(null);
    try {
      await onSave({
        user_id: Number(who),
        punch_in_time: pin,
        punch_out_time: pout,
        change_reason: v.reason.trim(),
      });
      reset();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Frame
      open={open}
      title={T('Legg til registrering')}
      onClose={() => {
        reset();
        onClose();
      }}
      testid="add-registration"
    >
      {err && (
        <p className="kerrbox" role="alert">
          {err}
        </p>
      )}
      <p>
        {T('Registrer bekreftet arbeidstid. Den må godkjennes separat før den inngår i lønnsgrunnlaget.')}
      </p>
      <form
        className="kaddform"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <label>
          {T('Ansatt')}
          <select
            className="inp"
            value={v.who || start.who}
            onChange={(e) => setV({ ...v, who: e.target.value })}
          >
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {fullName(p)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {T('Stemplet inn')}
          <input
            className="inp"
            type="datetime-local"
            aria-describedby={suggested ? 'kadd-suggested' : undefined}
            value={v.in}
            onChange={(e) => setV({ ...v, in: e.target.value })}
          />
        </label>
        <label>
          {T('Stemplet ut')}
          <input
            className="inp"
            type="datetime-local"
            aria-describedby={suggested ? 'kadd-suggested' : undefined}
            value={v.out}
            onChange={(e) => setV({ ...v, out: e.target.value })}
          />
        </label>
        {suggested && (
          <p className="khint" id="kadd-suggested">
            {T('Tidene er bare et forslag. Skriv inn de bekreftede tidene.')}
          </p>
        )}
        {suggested && (
          <label className="kcheck">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              data-testid="kadd-confirm"
            />
            {T('Tidene stemmer')}
          </label>
        )}
        <p className="khint">{T('Pausetrekket regnes etter bedriftens pauseregel.')}</p>
        <label>
          {T('Begrunnelse')}
          <textarea
            className="inp"
            rows={3}
            maxLength={500}
            value={v.reason}
            onChange={(e) => setV({ ...v, reason: e.target.value })}
          />
        </label>
        <div className="kdlgf">
          <Button
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {T('Avbryt')}
          </Button>
          <Button tone="pri" disabled={saving} onClick={save}>
            {saving ? T('Lagrer…') : T('Lagre registrering')}
          </Button>
        </div>
      </form>
    </Frame>
  );
}
